import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Save, Printer, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const isEmpty = (v) => v === undefined || v === null || String(v).trim() === '';
const orderQty = (par, soh) => Math.max(0, Math.round(((par || 0) - soh) * 100) / 100);

const Message = ({ text }) => (
  <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 text-center" data-testid="shared-stocktake-error">
    <img src="/logo.png" alt="The Merchant Coffee" className="w-48 h-auto" />
    <AlertCircle className="w-8 h-8 text-muted-foreground" />
    <p className="text-muted-foreground max-w-sm">{text}</p>
  </div>
);

export default function SharedStockTake() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [counts, setCounts] = useState({});
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/stock-take/${token}`)
      .then((res) => {
        setData(res.data);
        setCounts(Object.fromEntries(Object.entries(res.data.counts || {}).map(([k, v]) => [k, String(v)])));
        setName(res.data.counted_by || '');
        setSavedAt(res.data.updated_at);
      })
      .catch((e) => setError(e.response?.data?.detail || 'Could not load this stock take.'));
  }, [token]);

  const save = async () => {
    if (!name.trim()) { setShowErrors(true); toast.error('Please enter your name before saving'); document.querySelector('[data-testid="counter-name-input"]')?.focus(); return; }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/public/stock-take/${token}`, { name, counts });
      setSavedAt(res.data.updated_at);
      toast.success(`Saved (${res.data.count} of ${data.items.length} counted)`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  if (error) return <Message text={error} />;
  if (!data) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  const counted = data.items.filter((i) => !isEmpty(counts[i.stock_item_id])).length;
  const nameMissing = showErrors && !name.trim();

  return (
    <div className="min-h-screen bg-background p-4 sm:p-8 max-w-4xl mx-auto space-y-6" data-testid="shared-stocktake-page">
      <div className="flex items-center gap-4">
        <img src="/logo.png" alt="The Merchant Coffee" className="w-28 h-auto print:w-20" />
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="shared-stocktake-title">
            Stock Take: {data.supplier_name}
          </h1>
          <p className="text-sm text-muted-foreground">{data.branch_name} · {data.day_of_week} {new Date(data.for_date).toLocaleDateString()}</p>
          <p className="text-xs text-muted-foreground print:hidden" data-testid="shared-stocktake-expiry">Link valid until {new Date(data.expires_at).toLocaleString()}</p>
        </div>
      </div>

      <div className="space-y-2 max-w-sm">
        <Label htmlFor="counter-name">Your name *</Label>
        <Input id="counter-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Who is counting?"
          className={nameMissing ? 'border-red-500 ring-2 ring-red-300' : ''} data-testid="counter-name-input" />
        {nameMissing && <p className="text-xs text-red-600" data-testid="counter-name-required">Name is required</p>}
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Item</TableHead>
            <TableHead>Unit</TableHead>
            <TableHead className="text-center">PAR</TableHead>
            <TableHead className="text-center">Stock on Hand</TableHead>
            <TableHead className="text-center">Order</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.items.map((item) => {
            const raw = counts[item.stock_item_id];
            return (
              <TableRow key={item.stock_item_id}>
                <TableCell>
                  <p className="font-medium">{item.name}</p>
                  {item.notes && <p className="text-xs text-muted-foreground italic">{item.notes}</p>}
                </TableCell>
                <TableCell>{item.unit}</TableCell>
                <TableCell className="text-center font-medium">{item.par}</TableCell>
                <TableCell className="text-center">
                  <Input type="number" inputMode="decimal" step="0.01" min="0" value={raw ?? ''}
                    onChange={(e) => setCounts((c) => ({ ...c, [item.stock_item_id]: e.target.value }))}
                    className="w-24 mx-auto text-center print:border-0" data-testid={`shared-soh-input-${item.stock_item_id}`} />
                </TableCell>
                <TableCell className="text-center" data-testid={`shared-order-qty-${item.stock_item_id}`}>
                  {isEmpty(raw) ? '-' : orderQty(item.par, parseFloat(raw) || 0)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <div className="flex flex-wrap items-center gap-2 print:hidden">
        <span className="text-sm text-muted-foreground mr-auto" data-testid="shared-count-progress">
          {counted} of {data.items.length} counted{savedAt ? ` · last saved ${new Date(savedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}
        </span>
        <Button variant="outline" onClick={() => window.print()} data-testid="shared-print-btn"><Printer className="w-4 h-4 mr-2" />Print / PDF</Button>
        <Button onClick={save} disabled={saving} data-testid="shared-save-btn"><Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save'}</Button>
      </div>
      <p className="hidden print:block text-sm">Counted by: {name || '________________'}</p>
      <p className="text-xs text-muted-foreground print:hidden">Saving sends your counts to the manager, who will check and submit them. You can come back to this link and update your counts until then.</p>
    </div>
  );
}
